import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, FolderOpen, Loader2, Plus, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FissaggiTettoDialog from "@/components/FissaggiTettoDialog";
import ZavorreDialog from "@/components/ZavorreDialog";
import { COPPER } from "./VestaAuth";
import { useVestaMe } from "./VestaViewAs";
import { useVestaView } from "./vestaStore";
import { PraticaInfo } from "./VestaPraticaInfo";
import { CfgList } from "./VestaCfgList";
import { VestaHeader } from "./VestaHome";

const dt = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const TIPO_CT = { fissaggi_inclinato: "Fissaggi tetto inclinato", fissaggi_piano: "Fissaggi tetto piano", zavorre: "Zavorre" };
const TOOL_CFG = { consulenza: "Chiavi in mano", bolletta: "DEM bolletta", tetto: "Misura il tetto", colonnina: "Stazione di ricarica" };
const DIREZ = "torri_direzionale";  // Divisione Impianti Torri: pseudo-partner, nei tool si comporta come vista rete admin

const usePartner = () => useVestaMe()?.partner || null;

function Fiss({ open, onOpenChange, tipo, preload }) {
  const me = useVestaMe();
  const p = me?.partner || null;
  if (!p) return null;
  const adminAll = !!me?.admin && (!p.id || p.id === DIREZ);  // vista rete / Divisione Impianti: vede TUTTE le consulenze (backend scoping)
  return (
    <FissaggiTettoDialog open={open} onOpenChange={onOpenChange} installerId={p.id} installerName={p.name} installerIsPartner adminAll={adminAll} moduleRow={null} tettoConfig={null}
      forceTipo={tipo} inclinatoOnly={tipo !== "piano"} pianoOnly={tipo === "piano"} pdfMode preload={preload?.payload} preloadKey={preload?.key}
      onApply={() => { toast.success("Dimensionamento calcolato"); onOpenChange(false); }} />
  );
}
export const FissInclinatoDialog = (props) => <Fiss {...props} tipo="coppo_tegola" />;
export const FissPianoDialog = (props) => <Fiss {...props} tipo="piano" />;
export function ZavorreVestaDialog({ open, onOpenChange, preload }) {
  const me = useVestaMe();
  const p = me?.partner || null;
  if (!p) return null;
  const adminAll = !!me?.admin && (!p.id || p.id === DIREZ);  // vista rete / Divisione Impianti: vede TUTTE le consulenze (backend scoping)
  return <ZavorreDialog open={open} onOpenChange={onOpenChange} installerId={p.id} installerName={p.name} installerIsPartner adminAll={adminAll} preload={preload?.payload} preloadKey={preload?.key} />;
}

export const openPdf = async (id) => {
  const w = window.open("", "_blank");
  try { const r = await api.get(`/consulenze-tecniche/${id}/pdf`, { responseType: "blob", timeout: 120000 }); const u = URL.createObjectURL(r.data); if (w) w.location.href = u; else window.location.href = u; }
  catch { w?.close(); toast.error("PDF non disponibile"); }
};

const Row = ({ title, sub, children, testid, below }) => (
  <div className="rounded-xl border border-neutral-800 p-3" data-testid={testid}>
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{title}</div><div className="truncate text-xs text-neutral-400">{sub}</div></div>
      <div className="flex shrink-0 gap-2">{children}</div>
    </div>
    {below}
  </div>
);
const btn = "inline-flex items-center gap-1 rounded-full border border-neutral-700 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59]";

export function ConfigurazioniDialog({ open, onOpenChange, only, focus, tool }) {
  const me = useVestaMe();
  const p = me?.partner || null;
  const torri = !!(me?.admin || me?.viewing_as);
  const [rk, setRk] = useState(0);
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [ct, setCt] = useState(null);
  const [pre, setPre] = useState(null);
  const direz = p?.id === DIREZ;
  const net = !!p && (!p.id || direz);
  const fc = (cfg || []).find((c) => c.id === focus?.id);
  useVestaView(open, { scheda: "configurazioni del configuratore (consulenze Chiavi in mano, analisi bolletta, dimensionamento tetto, wallbox)", ...(fc ? { pratica_cliente: (fc.summary || {}).cliente, tool: fc.tool } : {}) });
  useEffect(() => { if (fc) setTimeout(() => document.querySelector(`[data-testid="vesta-tools-cfg-${fc.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 250); }, [fc]);
  useEffect(() => {
    if (!open || !p) return;
    if (!rk) { setCfg(null); setCt(null); }
    if (only !== "tecnici") api.get("/rete/configs", { params: { installer_id: net ? undefined : p.id, ...(tool ? { tool } : {}) } }).then((r) => setCfg(r.data.configs || [])).catch(() => setCfg([]));
    if (only !== "commerciali" && !rk) api.get("/consulenze-tecniche", { params: { installer_id: net ? undefined : p.id } }).then((r) => setCt(r.data.consulenze || [])).catch(() => setCt([]));
  }, [open, p?.id, only, tool, rk]); // eslint-disable-line react-hooks/exhaustive-deps
  const reopen = async (c) => {
    try { const r = await api.get(`/consulenze-tecniche/${c.id}`); setPre({ tipo: r.data.tipo, payload: r.data.payload || {}, key: c.id }); }
    catch { toast.error("Impossibile riaprire la configurazione"); }
  };
  const Pre = pre?.tipo === "zavorre" ? ZavorreVestaDialog : pre?.tipo === "fissaggi_piano" ? FissPianoDialog : FissInclinatoDialog;
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[88vh] max-w-3xl grid-cols-[minmax(0,1fr)] overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="vesta-tools-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-xl" style={{ color: COPPER }}><FolderOpen className="h-6 w-6" />{tool ? `${TOOL_CFG[tool] || tool} · ${net ? "rete" : "le mie configurazioni"}` : net ? "Configurazioni della rete" : "Le mie configurazioni"}</DialogTitle>
            <DialogDescription className="text-[11px] uppercase tracking-widest text-neutral-500" data-testid="vesta-tools-cfg-scope">{direz ? "Divisione Impianti Torri · tutti i database" : net ? "Vista rete · tutti i Partner" : "Dal database rete del configuratore · solo i dati della tua azienda"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {tool && !net && <button type="button" onClick={() => { onOpenChange(false); nav(`/vesta/configuratore?tool=${tool}`); }} data-testid="vesta-tools-cfg-new"
              className="flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-black transition-transform active:scale-[.98]" style={{ background: COPPER }}><Plus className="h-4 w-4" />Nuova configurazione · {TOOL_CFG[tool]}</button>}
            {only !== "tecnici" && <section>
              {!tool && <div className="mb-2 text-xs font-bold uppercase tracking-widest" style={{ color: COPPER }}>Tools commerciali</div>}
              {!cfg ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /> : cfg.length === 0 ? <p className="text-sm text-neutral-500" data-testid="vesta-tools-cfg-empty">Nessuna configurazione salvata.</p> : (
                <CfgList cfg={cfg} net={net} focusId={focus?.id} toolLabels={TOOL_CFG} torri={torri} reload={() => setRk((x) => x + 1)}
                  onOpen={!net && ((c) => { onOpenChange(false); nav(c.tool ? `/vesta/configuratore?tool=${c.tool}&config_id=${encodeURIComponent(c.id)}` : "/vesta/configuratore"); })} />
              )}
            </section>}
            {only !== "commerciali" && <section>
              <div className="mb-2 text-xs font-bold uppercase tracking-widest" style={{ color: COPPER }}>Tools tecnici</div>
              {!ct ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /> : ct.length === 0 ? <p className="text-sm text-neutral-500" data-testid="vesta-tools-ct-empty">Nessun calcolo salvato.</p> : (
                <div className="space-y-2">{ct.map((c) => (
                  <Row key={c.id} testid={`vesta-tools-ct-${c.id}`} title={`${TIPO_CT[c.tipo] || c.tipo}${c.cliente ? ` · ${c.cliente}` : ""}`}
                    sub={[net && c.installer_name, c.comune, c.potenza_kwp && `${c.potenza_kwp} kWp`, dt(c.created_at)].filter(Boolean).join(" · ")}>
                    <PraticaInfo kind="ct" id={c.id} testid={`vesta-tools-ct-info-${c.id}`} />
                    {!net && <button type="button" className={btn} onClick={() => reopen(c)} data-testid={`vesta-tools-ct-reopen-${c.id}`}><RotateCcw className="h-3.5 w-3.5" />Riapri</button>}
                    {c.pdf_storage_path !== undefined || c.pdf_filename ? <button type="button" className={btn} onClick={() => openPdf(c.id)} data-testid={`vesta-tools-ct-pdf-${c.id}`}><Download className="h-3.5 w-3.5" />PDF</button> : null}
                  </Row>
                ))}</div>
              )}
            </section>}
          </div>
        </DialogContent>
      </Dialog>
      {pre && <Pre open onOpenChange={(v) => !v && setPre(null)} preload={pre} />}
    </>
  );
}

export function VestaConfiguratore() {
  const [sp] = useSearchParams();
  const p = usePartner();
  const tool = sp.get("tool") || "";
  const configId = sp.get("config_id") || "";
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!p) return;
    setUrl(null);
    api.post("/rete/launch", { installer_id: p.id, tool, config_id: configId || undefined }).then((r) => setUrl(r.data.url)).catch((e) => setErr(e.response?.data?.detail || "Impossibile aprire il configuratore"));
  }, [p, tool, configId]);
  return (
    <div className="flex h-screen flex-col bg-black text-white" data-testid="vesta-configuratore-page">
      <VestaHeader />
      <div className="flex items-center justify-between gap-2 border-b border-neutral-900 px-4 py-2 sm:px-10">
        <Link to={`/vesta/slot/${TOOL_CFG[tool] ? "chiavi-in-mano" : "cassetta-attrezzi"}`} className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-300 hover:text-white" data-testid="vesta-cfg-back"><ArrowLeft className="h-3.5 w-3.5" /> {TOOL_CFG[tool] ? "Torna a Chiavi in mano" : "Torna ai Tools"}</Link>
        <span className="truncate text-xs text-neutral-500">Configuratore{TOOL_CFG[tool] ? ` · ${TOOL_CFG[tool]}` : ""}{p ? ` · ${p.name}` : ""}</span>
      </div>
      <div className="relative min-h-0 flex-1">
        {err ? <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-red-400" data-testid="vesta-cfg-error"><AlertTriangle className="h-6 w-6" />{err}</div>
          : !url ? <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: COPPER }} /></div>
          : <iframe src={url} title="Configuratore Torri" className="h-full w-full border-0 bg-white" allow="geolocation; clipboard-write" data-testid="vesta-cfg-iframe" />}
      </div>
    </div>
  );
}
